import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, TrendingUp, TrendingDown, DollarSign, BarChart3,
  Zap, AlertTriangle, ArrowUpRight, ArrowDownRight, Clock,
  Flame, Target, Eye, Bell, Star, ChevronDown,
} from 'lucide-react';

// ─── The Upsell Exchange — Your earnings as a live trading floor ─────
// Stolen shamelessly from Robinhood, Coinbase, and crypto Twitter

// ─── Mock Data ──────────────────────────────────────────────────────

interface PricePoint {
  time: string;
  value: number;
}

// "Stock price" = rolling average earnings per shift
const PRICE_HISTORY: PricePoint[] = [
  { time: '8 weeks ago', value: 142 },
  { time: '7 weeks ago', value: 148 },
  { time: '6 weeks ago', value: 139 },
  { time: '5 weeks ago', value: 156 },
  { time: '4 weeks ago', value: 168 },
  { time: '3 weeks ago', value: 175 },
  { time: '2 weeks ago', value: 189 },
  { time: 'Last week', value: 198 },
  { time: 'This week', value: 212 },
];

interface Asset {
  ticker: string;
  name: string;
  price: number;
  change24h: number;
  changePct: number;
  volume: number;
  high: number;
  low: number;
  marketCap: string;
  sparkline: number[];
}

const YOUR_PORTFOLIO: Asset[] = [
  {
    ticker: '$WAGYU',
    name: 'Wagyu Platter Upsell',
    price: 14.20,
    change24h: 2.80,
    changePct: 24.6,
    volume: 47,
    high: 16.50,
    low: 8.40,
    marketCap: '834 pts',
    sparkline: [8, 9, 11, 10, 12, 14, 13, 15, 14],
  },
  {
    ticker: '$DONER',
    name: 'Döner Kebab Upsell',
    price: 11.80,
    change24h: 1.20,
    changePct: 11.3,
    volume: 34,
    high: 13.00,
    low: 7.20,
    marketCap: '612 pts',
    sparkline: [7, 8, 7, 9, 10, 11, 10, 12, 12],
  },
  {
    ticker: '$DUBAI',
    name: 'Dubai Chocolate Upsell',
    price: 9.40,
    change24h: 3.10,
    changePct: 49.2,
    volume: 28,
    high: 10.20,
    low: 4.80,
    marketCap: '423 pts',
    sparkline: [5, 5, 6, 6, 7, 7, 8, 9, 9],
  },
  {
    ticker: '$MANGO',
    name: 'Mango Lassi Promo',
    price: 4.80,
    change24h: -0.40,
    changePct: -7.7,
    volume: 19,
    high: 6.20,
    low: 3.80,
    marketCap: '187 pts',
    sparkline: [5, 6, 5, 6, 5, 4, 5, 5, 5],
  },
  {
    ticker: '$GREET',
    name: 'Greeting Quality',
    price: 3.20,
    change24h: 0.30,
    changePct: 10.3,
    volume: 92,
    high: 3.50,
    low: 2.40,
    marketCap: '312 pts',
    sparkline: [2, 2, 3, 3, 3, 3, 3, 3, 3],
  },
];

interface TradeEvent {
  time: string;
  action: 'BUY' | 'SELL' | 'HOLD' | 'MOON';
  ticker: string;
  detail: string;
  pnl?: string;
}

const RECENT_TRADES: TradeEvent[] = [
  { time: '12:45 PM', action: 'BUY', ticker: '$DUBAI', detail: 'Guest added Dubai Chocolate dessert x2', pnl: '+$9.40' },
  { time: '12:30 PM', action: 'MOON', ticker: '$WAGYU', detail: 'Table of 6 — ALL ordered Wagyu!', pnl: '+$42.60' },
  { time: '12:15 PM', action: 'HOLD', ticker: '$DONER', detail: 'Pitched but guest wasn\'t hungry enough', pnl: '+$2.00' },
  { time: '12:01 PM', action: 'BUY', ticker: '$GREET', detail: 'Recognized a regular — perfect greeting', pnl: '+$3.20' },
  { time: '11:52 AM', action: 'BUY', ticker: '$MANGO', detail: 'Sold 2 Mango Lassis to table 4', pnl: '+$4.80' },
  { time: '11:38 AM', action: 'BUY', ticker: '$WAGYU', detail: 'First Wagyu of the shift — clean close', pnl: '+$14.20' },
];

const actionColors = {
  BUY: 'text-emerald-400 bg-emerald-400/10',
  SELL: 'text-red-400 bg-red-400/10',
  HOLD: 'text-amber-400 bg-amber-400/10',
  MOON: 'text-cyan-400 bg-cyan-400/10',
};

// ─── Component ──────────────────────────────────────────────────────

const UpsellExchange: React.FC = () => {
  const navigate = useNavigate();
  const [timeframe, setTimeframe] = useState<'1D' | '1W' | '1M' | '3M'>('1M');
  const [tickerFlash, setTickerFlash] = useState<Record<string, 'up' | 'down' | null>>({});

  // Simulate live price updates
  useEffect(() => {
    const interval = setInterval(() => {
      const randomAsset = YOUR_PORTFOLIO[Math.floor(Math.random() * YOUR_PORTFOLIO.length)];
      const direction = Math.random() > 0.3 ? 'up' : 'down';
      setTickerFlash(prev => ({ ...prev, [randomAsset.ticker]: direction }));
      setTimeout(() => {
        setTickerFlash(prev => ({ ...prev, [randomAsset.ticker]: null }));
      }, 600);
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  // Main chart
  const chartMax = Math.max(...PRICE_HISTORY.map(p => p.value));
  const chartMin = Math.min(...PRICE_HISTORY.map(p => p.value));
  const chartRange = chartMax - chartMin;
  const totalChange = PRICE_HISTORY[PRICE_HISTORY.length - 1].value - PRICE_HISTORY[0].value;
  const totalPct = ((totalChange / PRICE_HISTORY[0].value) * 100).toFixed(1);
  const isUp = totalChange >= 0;

  // SVG path for main chart
  const chartWidth = 600;
  const chartHeight = 200;
  const points = PRICE_HISTORY.map((p, i) => {
    const x = (i / (PRICE_HISTORY.length - 1)) * chartWidth;
    const y = chartHeight - ((p.value - chartMin) / chartRange) * (chartHeight - 20) - 10;
    return `${x},${y}`;
  });
  const linePath = `M ${points.join(' L ')}`;
  const areaPath = `${linePath} L ${chartWidth},${chartHeight} L 0,${chartHeight} Z`;

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Top bar — exchange style */}
      <div className="bg-gray-950 border-b border-gray-900">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between">
          <button onClick={() => navigate('/restaurant/mockups')} className="flex items-center gap-1.5 text-gray-500 hover:text-gray-300 text-sm transition-colors">
            <ArrowLeft className="w-4 h-4" /> Hub
          </button>
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            <span className="text-xs text-gray-500">MARKETS OPEN</span>
          </div>
          <div className="flex items-center gap-3 text-xs text-gray-600">
            <span>Gen Korean BBQ</span>
            <span className="text-gray-800">|</span>
            <span>Lunch Shift</span>
          </div>
        </div>
      </div>

      {/* Scrolling ticker tape */}
      <div className="bg-gray-950 border-b border-gray-900 overflow-hidden">
        <div className="flex items-center gap-8 py-1.5 animate-marquee whitespace-nowrap px-4">
          {[...YOUR_PORTFOLIO, ...YOUR_PORTFOLIO].map((asset, i) => (
            <span key={i} className="flex items-center gap-2 text-xs">
              <span className="font-bold text-gray-400">{asset.ticker}</span>
              <span className="text-white">${asset.price.toFixed(2)}</span>
              <span className={asset.changePct >= 0 ? 'text-emerald-400' : 'text-red-400'}>
                {asset.changePct >= 0 ? '+' : ''}{asset.changePct.toFixed(1)}%
              </span>
            </span>
          ))}
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        {/* Portfolio value hero */}
        <div>
          <div className="flex items-center gap-2 mb-1">
            <h1 className="text-sm text-gray-500">YOUR PORTFOLIO VALUE</h1>
          </div>
          <div className="flex items-baseline gap-3 mb-1">
            <span className="text-4xl font-bold">$212.00</span>
            <span className={`text-sm font-medium flex items-center gap-1 ${isUp ? 'text-emerald-400' : 'text-red-400'}`}>
              {isUp ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
              ${Math.abs(totalChange).toFixed(2)} ({totalPct}%)
            </span>
          </div>
          <p className="text-xs text-gray-600">Average earnings per shift · All time</p>
        </div>

        {/* Main chart */}
        <div className="bg-gray-950 rounded-xl border border-gray-900 p-4">
          <svg viewBox={`0 0 ${chartWidth} ${chartHeight}`} className="w-full h-48" preserveAspectRatio="none">
            <defs>
              <linearGradient id="chartGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={isUp ? '#22c55e' : '#ef4444'} stopOpacity="0.3" />
                <stop offset="100%" stopColor={isUp ? '#22c55e' : '#ef4444'} stopOpacity="0" />
              </linearGradient>
            </defs>
            <path d={areaPath} fill="url(#chartGrad)" />
            <path d={linePath} fill="none" stroke={isUp ? '#22c55e' : '#ef4444'} strokeWidth="2.5" />
            {/* Latest point dot */}
            {(() => {
              const lastPoint = points[points.length - 1].split(',');
              return (
                <circle cx={lastPoint[0]} cy={lastPoint[1]} r="4" fill={isUp ? '#22c55e' : '#ef4444'}>
                  <animate attributeName="r" values="4;6;4" dur="2s" repeatCount="indefinite" />
                </circle>
              );
            })()}
          </svg>
          <div className="flex items-center gap-2 mt-3">
            {(['1D', '1W', '1M', '3M'] as const).map((tf) => (
              <button
                key={tf}
                onClick={() => setTimeframe(tf)}
                className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                  timeframe === tf ? 'bg-gray-800 text-white' : 'text-gray-600 hover:text-gray-400'
                }`}
              >
                {tf}
              </button>
            ))}
          </div>
        </div>

        {/* Portfolio / Watchlist */}
        <div>
          <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3">Your Holdings</h2>
          <div className="bg-gray-950 rounded-xl border border-gray-900 overflow-hidden divide-y divide-gray-900">
            {YOUR_PORTFOLIO.map((asset) => {
              const flash = tickerFlash[asset.ticker];
              // Mini sparkline
              const sparkMax = Math.max(...asset.sparkline);
              const sparkMin = Math.min(...asset.sparkline);
              const sparkRange = sparkMax - sparkMin || 1;
              const sparkPoints = asset.sparkline.map((v, i) =>
                `${(i / (asset.sparkline.length - 1)) * 60},${30 - ((v - sparkMin) / sparkRange) * 25}`
              ).join(' ');

              return (
                <div
                  key={asset.ticker}
                  className={`flex items-center gap-4 px-4 py-3 transition-all duration-300 ${
                    flash === 'up' ? 'bg-emerald-500/5' : flash === 'down' ? 'bg-red-500/5' : ''
                  }`}
                >
                  {/* Ticker */}
                  <div className="w-20 flex-shrink-0">
                    <div className="text-sm font-bold text-white">{asset.ticker}</div>
                    <div className="text-[10px] text-gray-600 truncate">{asset.name}</div>
                  </div>

                  {/* Sparkline */}
                  <div className="w-16 flex-shrink-0 hidden sm:block">
                    <svg viewBox="0 0 60 30" className="w-full h-8">
                      <polyline
                        points={sparkPoints}
                        fill="none"
                        stroke={asset.changePct >= 0 ? '#22c55e' : '#ef4444'}
                        strokeWidth="1.5"
                      />
                    </svg>
                  </div>

                  {/* Volume */}
                  <div className="flex-1 hidden sm:block">
                    <div className="text-[10px] text-gray-600">VOL</div>
                    <div className="text-xs text-gray-400">{asset.volume}</div>
                  </div>

                  {/* Market cap (points) */}
                  <div className="hidden sm:block">
                    <div className="text-[10px] text-gray-600">MKTCAP</div>
                    <div className="text-xs text-gray-400">{asset.marketCap}</div>
                  </div>

                  {/* Price + change */}
                  <div className="text-right flex-shrink-0 w-24">
                    <div className={`text-sm font-bold transition-colors ${
                      flash === 'up' ? 'text-emerald-300' : flash === 'down' ? 'text-red-300' : 'text-white'
                    }`}>
                      ${asset.price.toFixed(2)}
                    </div>
                    <div className={`text-xs font-medium ${asset.changePct >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                      {asset.changePct >= 0 ? '+' : ''}{asset.changePct.toFixed(1)}%
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Trade log */}
        <div>
          <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3">Today's Trades</h2>
          <div className="bg-gray-950 rounded-xl border border-gray-900 overflow-hidden divide-y divide-gray-900">
            {RECENT_TRADES.map((trade, i) => {
              const ac = actionColors[trade.action];
              return (
                <div key={i} className="flex items-center gap-3 px-4 py-3">
                  <span className={`text-[10px] font-black px-1.5 py-0.5 rounded ${ac}`}>
                    {trade.action}
                  </span>
                  <span className="text-xs font-bold text-gray-300 w-14 flex-shrink-0">{trade.ticker}</span>
                  <span className="text-xs text-gray-500 flex-1 truncate">{trade.detail}</span>
                  <span className="text-xs text-gray-600 flex-shrink-0 hidden sm:block">{trade.time}</span>
                  {trade.pnl && (
                    <span className="text-xs font-bold text-emerald-400 flex-shrink-0">{trade.pnl}</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Degen section */}
        <div className="bg-gradient-to-r from-cyan-500/5 to-purple-500/5 border border-cyan-500/20 rounded-xl p-5">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-lg">&#x1f680;</span>
            <h3 className="text-sm font-bold text-cyan-400">ALPHA ALERT</h3>
          </div>
          <div className="space-y-2 text-xs">
            <p className="text-gray-400">
              <span className="font-bold text-white">$DUBAI is pumping.</span> Dubai Chocolate upsell success rate
              is up 49.2% this week. The dinner crowd hasn't discovered it yet. <span className="text-cyan-400">Early mover advantage.</span>
            </p>
            <p className="text-gray-500">
              <span className="text-amber-400 font-bold">NFA</span> but the servers who leaned into $DUBAI last week
              averaged $23 more per shift. <span className="text-gray-400">WAGMI.</span>
            </p>
          </div>
        </div>

        {/* Bottom stats */}
        <div className="grid grid-cols-3 gap-3 pb-6">
          <div className="bg-gray-950 rounded-lg border border-gray-900 p-4 text-center">
            <div className="text-2xl font-bold text-emerald-400">$76.20</div>
            <div className="text-[10px] text-gray-600 uppercase">Today's P&L</div>
          </div>
          <div className="bg-gray-950 rounded-lg border border-gray-900 p-4 text-center">
            <div className="text-2xl font-bold text-white">6</div>
            <div className="text-[10px] text-gray-600 uppercase">Trades Executed</div>
          </div>
          <div className="bg-gray-950 rounded-lg border border-gray-900 p-4 text-center">
            <div className="text-2xl font-bold text-cyan-400">83%</div>
            <div className="text-[10px] text-gray-600 uppercase">Win Rate</div>
          </div>
        </div>
      </div>

      {/* Marquee animation */}
      <style>{`
        @keyframes marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .animate-marquee {
          animation: marquee 20s linear infinite;
        }
      `}</style>
    </div>
  );
};

export default UpsellExchange;
